import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosServices from '../../utils/axios';

// Initial state
const initialState = {
    uploadedFiles: [],
    currentUpload: {
        loading: false,
        error: null,
        data: null
    },
    fileDetails: {
        data: null,
        loading: false,
        error: null
    },
    filePreview: {
        data: null,
        loading: false,
        error: null
    }
};

// Dosya detaylarını getirmek için yeni async thunk
export const getFileDetails = createAsyncThunk(
    'file/getFileDetails',
    async (idFile, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/file-service/api/Files/${idFile}`
            );

            if (!response.data?.data) {
                throw new Error('Dosya detayları alınamadı');
            }

            return response.data.data;

        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.response?.data?.exceptions?.[0]?.Message ||
                'Dosya detayları alınamadı'
            );
        }
    }
);

// Dosya yükleme işlemi için async thunk
export const uploadFile = createAsyncThunk(
    'file/upload',
    async ({ file, flStatic = false, idFileType = 1, saveWithOriginalName = true, componentId = null }, { rejectWithValue }) => {
        try {
            // FormData oluştur
            const formData = new FormData();
            formData.append('File', file);
            formData.append('FlStatic', flStatic);
            formData.append('IdFileType', idFileType);
            formData.append('SaveWithOriginalName', saveWithOriginalName);

            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/file-service/api/Files/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (!response.data?.data) {
                throw new Error('Dosya yükleme başarısız');
            }

            return {
                idFile: response.data.data.idFile,
                url: response.data.data.url,
                name: response.data.data.name,
                originalName: file.name, // Orijinal dosya adını da saklayalım
                componentId: componentId // Komponent ID'sini ekle
            };

        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.response?.data?.exceptions?.[0]?.Message ||
                'Dosya yükleme sırasında bir hata oluştu'
            );
        }
    }
);

// Dosya önizleme için yeni async thunk
export const getFilePreview = createAsyncThunk(
    'file/getFilePreview',
    async (idFile, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/file-service/api/Files/get/${idFile}`
            );

            if (!response.data?.data?.file) {
                throw new Error('Dosya önizleme verisi alınamadı');
            }

            // Base64 verisini data URL formatına çevirme
            // return `data:image/jpeg;base64,${response.data.data.file}`;
            return response.data.data;

        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.response?.data?.exceptions?.[0]?.Message ||
                'Dosya önizleme verisi alınamadı'
            );
        }
    }
);

// FileSlice
const fileSlice = createSlice({
    name: 'file',
    initialState,
    reducers: {
        clearUploadError: (state) => {
            state.currentUpload.error = null;
        },
        clearUploadData: (state) => {
            state.currentUpload.data = null;
        },
        removeUploadedFile: (state, action) => {
            state.uploadedFiles = state.uploadedFiles.filter(
                file => file.idFile !== action.payload
            );
        },
        clearFileDetails: (state) => {
            state.fileDetails = {
                data: null,
                loading: false,
                error: null
            };
        },
        clearFilePreview: (state) => {
            state.filePreview = {
                data: null,
                loading: false,
                error: null
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // Upload cases
            .addCase(uploadFile.pending, (state) => {
                state.currentUpload.loading = true;
                state.currentUpload.error = null;
            })
            .addCase(uploadFile.fulfilled, (state, action) => {
                state.currentUpload.loading = false;
                state.currentUpload.data = action.payload;
                // Yüklenen dosyayı listeye ekle
                state.uploadedFiles.push(action.payload);
            })
            .addCase(uploadFile.rejected, (state, action) => {
                state.currentUpload.loading = false;
                state.currentUpload.error = action.payload;
            })

            // Yeni getFileDetails cases
            .addCase(getFileDetails.pending, (state) => {
                state.fileDetails.loading = true;
                state.fileDetails.error = null;
            })
            .addCase(getFileDetails.fulfilled, (state, action) => {
                state.fileDetails.loading = false;
                state.fileDetails.data = action.payload;
                state.fileDetails.error = null;
            })
            .addCase(getFileDetails.rejected, (state, action) => {
                state.fileDetails.loading = false;
                state.fileDetails.error = action.payload;
                state.fileDetails.data = null;
            })

            // Yeni getFilePreview cases
            .addCase(getFilePreview.pending, (state) => {
                state.filePreview.loading = true;
                state.filePreview.error = null;
            })
            .addCase(getFilePreview.fulfilled, (state, action) => {
                state.filePreview.loading = false;
                state.filePreview.data = action.payload;
                state.filePreview.error = null;
            })
            .addCase(getFilePreview.rejected, (state, action) => {
                state.filePreview.loading = false;
                state.filePreview.error = action.payload;
                state.filePreview.data = null;
            });
    }
});

// Actions
export const { 
    clearUploadError, 
    clearUploadData, 
    removeUploadedFile,
    clearFileDetails,
    clearFilePreview 
} = fileSlice.actions;

// Selectors
export const selectCurrentUpload = (state) => state.file.currentUpload;
export const selectUploadedFiles = (state) => state.file.uploadedFiles;
export const selectFileDetails = (state) => state.file.fileDetails;
export const selectFilePreview = (state) => state.file.filePreview;

export default fileSlice.reducer; 